import { useTranslation } from 'react-i18next';

import ClientRow from './components/ClientRow/ClientRow';
import ClientsTableSkeleton from './components/ClientsTableSkeleton/ClientsTableSkeleton';
import ClientsTableState from './components/ClientsTableState/ClientsTableState';
import Pagination from '../../../../shared/components/Globals/Pagination/Pagination';
import Table from '../../../../shared/components/Globals/Table/Table';
import type { IClientsColumn, IClientsTableProps } from '../../../../shared/interfaces/clients';
import { TRANSLATIONS_GLOBAL } from '../../../../shared/translations/commons';
import { getTotalPages } from '../../../../shared/utils/clients/clients';
import { toKebabCase } from '../../../../shared/utils/utils';

import './ClientsTable.scss';

const ClientsTable = ({
  clients,
  columns,
  status,
  loading,
  error,
  query,
  sort,
  onSortChange,
  page,
  pageSize,
  total,
  onPageChange,
  onSelect,
  onRetry
}: IClientsTableProps) => {
  const { t } = useTranslation();

  const renderHeaderCell = ({ id, labelKey, sortKey }: IClientsColumn) => {
    const className = `clients-table__col--${toKebabCase(id)}`;

    if (!sortKey) {
      return (
        <Table.HeaderCell key={id} className={className}>
          {t(labelKey)}
        </Table.HeaderCell>
      );
    }

    const active = sort.key === sortKey;
    const nextDirection = active && sort.direction === 'asc' ? 'descending' : 'ascending';

    return (
      <Table.SortableHeaderCell
        key={id}
        className={className}
        active={active}
        direction={sort.direction}
        onToggle={() => onSortChange(sortKey)}
        toggleLabel={`${t(labelKey)} — ${t(TRANSLATIONS_GLOBAL.sort[nextDirection])}`}
      >
        {t(labelKey)}
      </Table.SortableHeaderCell>
    );
  };

  if (loading) {
    return (
      <div className="clients-table__card">
        <ClientsTableSkeleton columnCount={columns.length} />
      </div>
    );
  }

  if (error || clients.length === 0) {
    return (
      <div className="clients-table__card">
        <ClientsTableState status={status} query={query} error={error} onRetry={onRetry} />
      </div>
    );
  }

  return (
    <>
      <div className="clients-table__card">
        <Table className="clients-table">
          <Table.Head>
            <Table.Row>
              {columns.map(renderHeaderCell)}
              <Table.HeaderCell className="clients-table__col--action" />
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {clients.map((client) => (
              <ClientRow key={client.id} client={client} columns={columns} onSelect={onSelect} />
            ))}
          </Table.Body>
        </Table>
      </div>

      <Pagination
        page={page}
        totalPages={getTotalPages(total, pageSize)}
        total={total}
        pageSize={pageSize}
        onPageChange={onPageChange}
      />
    </>
  );
};

export default ClientsTable;
